// client/src/components/Tasks/CreateTaskModal.jsx
import React, { useMemo, useState } from 'react';
import './CreateTaskModal.css';

function CreateTaskModal({ onClose, relatedId, relatedType, onCreated }) {
  const [form, setForm] = useState({
    title: '',
    type: 'Phone Call',
    date: '',
    startTime: '',
    endTime: '',
    phoneNumber: '',
    note: ''
  });

  const [submitting, setSubmitting] = useState(false);

  const showPhoneNumber = useMemo(
    () => form.type === 'Phone Call' || form.type === 'Meeting',
    [form.type]
  );

  const getUserTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';
  };

  const buildUtcDateTime = (dateValue, timeValue) => {
    if (!dateValue || !timeValue) return null;

    const [year, month, day] = dateValue.split('-').map(Number);
    const [hour, minute] = timeValue.split(':').map(Number);

    if (!year || !month || !day || Number.isNaN(hour) || Number.isNaN(minute)) {
      return null;
    }

    const localDate = new Date(year, month - 1, day, hour, minute, 0, 0);

    if (Number.isNaN(localDate.getTime())) {
      return null;
    }

    return localDate.toISOString();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const timezone = getUserTimezone();
    const dueAtUtc = buildUtcDateTime(form.date, form.startTime);
    const endAtUtc = buildUtcDateTime(form.date, form.endTime);

    const payload = {
      title: form.title,
      description: '',
      dueDate: form.date,
      startTime: form.startTime || null,
      endTime: form.endTime || null,
      timezone,
      dueAtUtc,
      endAtUtc,
      status: 'Pending',
      relatedId,
      relatedType,
      assignedTo: null,
      type: form.type,
      phoneNumber: showPhoneNumber ? form.phoneNumber : '',
      note: form.note
    };

    try {
      setSubmitting(true);

      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || 'Error creating task');
      }

      onCreated?.();
      onClose?.();
    } catch (err) {
      console.error('Error creating task:', err);
      alert('Error creating task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal create-task-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="task-modal-header">
          <div>
            <p className="task-modal-eyebrow">Task</p>
            <h2>Create Task</h2>
            <p className="task-modal-subtitle">
              Add a call, appointment, meeting, follow-up, or reminder.
            </p>
          </div>

          <button type="button" className="task-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="task-form-grid">
          <div className="form-row full-width">
            <label>Title</label>
            <input
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="Enter task title"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-column">
              <label>Type</label>
              <select name="type" value={form.type} onChange={handleChange}>
                <option>Phone Call</option>
                <option>Appointment</option>
                <option>Meeting</option>
                <option>Follow-Up</option>
                <option>Send Text</option>
                <option>Send Email</option>
                <option>Other</option>
              </select>
            </div>

            <div className="form-column">
              <label>Date</label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-column">
              <label>Start Time</label>
              <input
                type="time"
                name="startTime"
                value={form.startTime}
                onChange={handleChange}
              />
            </div>

            <div className="form-column">
              <label>End Time</label>
              <input
                type="time"
                name="endTime"
                value={form.endTime}
                onChange={handleChange}
              />
            </div>
          </div>

          {showPhoneNumber && (
            <div className="form-row full-width">
              <label>Phone Number</label>
              <input
                name="phoneNumber"
                value={form.phoneNumber}
                onChange={handleChange}
                placeholder="Enter phone number"
              />
            </div>
          )}

          <div className="form-row full-width">
            <label>
              {form.type === 'Meeting'
                ? 'Meeting Notes'
                : form.type === 'Phone Call'
                  ? 'Call Notes'
                  : 'Note'}
            </label>
            <textarea
              name="note"
              value={form.note}
              onChange={handleChange}
              placeholder="Add any task details or context"
              rows={5}
            />
          </div>

          <div className="modal-buttons task-modal-actions">
            <div />

            <div className="task-action-right">
              <button type="button" onClick={onClose}>
                Cancel
              </button>

              <button type="submit" disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Task'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateTaskModal;